import { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import DOMPurify from "dompurify";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, RefreshCw, Mail, ArrowLeft, ExternalLink, User, PenSquare, X, Flame, ShieldCheck, Inbox as InboxIcon, ListChecks, SendHorizontal, Bot, Linkedin, Clock, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";
import { ReplyCard, ApprovalCard } from "@/pages/InboxPage";
import { useAuth } from "@/lib/auth";

// Harden sanitized links: external email links open in a new tab without leaking
// the opener reference (prevents reverse-tabnabbing from malicious inbound emails).
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});


export default function M365InboxPage() {
  const [emails, setEmails] = useState([]);
  const [active, setActive] = useState(null);
  // Preserve the email-list scroll position across selection / re-renders (no jump-to-top).
  const listRef = useRef(null);
  const listScrollTop = useRef(0);
  const onListScroll = () => { if (listRef.current) listScrollTop.current = listRef.current.scrollTop; };
  useLayoutEffect(() => {
    if (listRef.current && listRef.current.scrollTop !== listScrollTop.current) {
      listRef.current.scrollTop = listScrollTop.current;
    }
  });
  const [detail, setDetail] = useState(null);
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  // Reply composer state
  const [replyTo, setReplyTo] = useState([]);
  const [replyCc, setReplyCc] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyGuidance, setReplyGuidance] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("attention");
  const [needsCount, setNeedsCount] = useState(0);
  // Merged triage + approvals (folded in from the old separate "Inbox" page)
  const [replies, setReplies] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [attentionItems, setAttentionItems] = useState([]);
  const [sent, setSent] = useState([]);
  const [sentFilter, setSentFilter] = useState("all");
  const [sentLoading, setSentLoading] = useState(false);
  const [sentActive, setSentActive] = useState(null);
  const [sentDetail, setSentDetail] = useState(null);
  const [sentReplyTo, setSentReplyTo] = useState([]);
  const [sentReplyCc, setSentReplyCc] = useState("");
  const [sentReplySubject, setSentReplySubject] = useState("");
  const [sentReplyBody, setSentReplyBody] = useState("");
  const [sentReplyGuidance, setSentReplyGuidance] = useState("");
  const [sentDraftBusy, setSentDraftBusy] = useState(false);
  const [sentSending, setSentSending] = useState(false);
  const [pendingDMs, setPendingDMs] = useState([]);
  const [dmSyncing, setDmSyncing] = useState(false);
  const [hideOldDMs, setHideOldDMs] = useState(false);
  const { user } = useAuth();
  const bookingLink = (user && user.default_meeting_link) || "";

  const loadDMs = useCallback(async () => {
    try {
      const { data } = await api.get("/linkedin-browser/inbox/pending-dms");
      setPendingDMs(data.pending || []);
    } catch { /* no-op */ }
  }, []);

  const syncDMs = async () => {
    setDmSyncing(true);
    try {
      await api.post("/linkedin-browser/inbox/sync");
      toast.success("Syncing your LinkedIn inbox… drafts appear here in ~1-2 min");
      setTimeout(loadDMs, 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setDmSyncing(false);
  };

  const sendDM = async (id, text) => {
    setBusyId(id);
    try {
      await api.post(`/linkedin-browser/inbox/dm/${id}/send`, { text });
      setPendingDMs((p) => p.filter((x) => x.id !== id));
      setReplies((prev) => prev.filter((p) => p.id !== id));
      setAttentionItems((prev) => prev.filter((x) => !(x.kind === "reply" && x.id === id)));
      toast.success("LinkedIn DM sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setBusyId(null);
  };

  const skipDM = async (id) => {
    try {
      await api.post(`/linkedin-browser/inbox/dm/${id}/dismiss`);
      setPendingDMs((p) => p.filter((x) => x.id !== id));
      setReplies((prev) => prev.filter((p) => p.id !== id));
      setAttentionItems((prev) => prev.filter((x) => !(x.kind === "reply" && x.id === id)));
    } catch { toast.error("Couldn't skip"); }
  };

  const regenerateDM = async (id, tone) => {
    const { data } = await api.post(`/linkedin-browser/inbox/dm/${id}/regenerate`, { tone });
    setPendingDMs((p) => p.map((x) => (x.id === id ? { ...x, drafts: { ...(x.drafts || {}), dm: data.text } } : x)));
    return data.text;
  };

  const loadSent = useCallback(async () => {
    setSentLoading(true);
    try {
      const { data } = await api.get(`/m365/sent?limit=150&kind=${sentFilter}`);
      setSent(data.sent || []);
    } catch { toast.error("Failed to load sent emails"); }
    setSentLoading(false);
  }, [sentFilter]);

  useEffect(() => { if (tab === "dms") loadDMs(); }, [tab, loadDMs]);
  useEffect(() => { if (tab === "sent") loadSent(); }, [tab, loadSent]);

  const openSent = async (id) => {
    setSentActive(id);
    setSentDetail(null);
    setSentReplyBody(""); setSentReplyCc(""); setSentReplyGuidance("");
    try {
      const { data } = await api.get(`/m365/sent/${id}`);
      setSentDetail(data);
      const to = (data.to && data.to.length) ? data.to : (data.prospect_email ? [data.prospect_email] : []);
      setSentReplyTo(to);
      setSentReplySubject(data.subject?.toLowerCase().startsWith("re:") ? data.subject : `Re: ${data.subject || ""}`);
    } catch { toast.error("Failed to load sent email"); }
  };

  const aiDraftSent = async () => {
    if (!sentActive) return;
    setSentDraftBusy(true);
    try {
      const { data } = await api.post(`/m365/sent/${sentActive}/ai-reply`, {
        guidance: sentReplyGuidance.trim() || undefined });
      setSentReplyBody(data.draft_text || "");
      if (data.subject) setSentReplySubject(data.subject);
      if (data.to?.length) setSentReplyTo(data.to);
      setSentReplyGuidance("");
      toast.success("AI follow-up ready — review then Send");
    } catch (e) {
      if (e?.response?.status === 429) toast.error("AI budget reached — adjust in AI Budget");
      else toast.error(`AI draft failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSentDraftBusy(false);
  };

  const sendFollowUp = async () => {
    if (!sentReplyTo.length || !sentReplyBody.trim()) { toast.error("Recipient + body required"); return; }
    setSentSending(true);
    try {
      const html = "<p>" + sentReplyBody.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>") + "</p>";
      const cc = sentReplyCc.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post(`/m365/sent/${sentActive}/follow-up`, {
        to: sentReplyTo, cc, html_body: html, subject: sentReplySubject,
        prospect_id: sentDetail?.prospect_id || undefined });
      toast.success("Follow-up sent via Microsoft 365");
      setSentReplyBody("");
      await loadSent();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSentSending(false);
  };

  const loadAttention = useCallback(async () => {
    try {
      const [le, r, d] = await Promise.all([
        api.get("/m365/inbox?lead_only=true&limit=50"),
        api.get("/inbox/replies?limit=50"),
        api.get("/dashboard"),
      ]);
      const emails = (le.data.messages || []).map((e) => ({ kind: "email", id: e.id, data: e, ts: e.received_at }));
      const reps = (r.data.replies || []).map((p) => ({ kind: "reply", id: p.id, data: p, ts: p.replied_at || p.updated_at }));
      const apps = (d.data?.approval_queue || []).map((p) => ({ kind: "approval", id: p.id, data: p, ts: p.updated_at }));
      const all = [...emails, ...reps, ...apps].map((it) => ({ ...it, score: scoreAttention(it) }));
      all.sort((a, b) => b.score - a.score || (b.ts || "").localeCompare(a.ts || ""));
      setAttentionItems(all);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { if (tab === "attention") loadAttention(); }, [tab, loadAttention]);

  const loadTriage = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        api.get("/inbox/replies?limit=50"),
        api.get("/dashboard"),
      ]);
      setReplies(r.data.replies || []);
      setApprovalQueue(d.data?.approval_queue || []);
    } catch { toast.error("Failed to load replies"); }
  }, []);

  useEffect(() => {
    if (tab === "triage" || tab === "approvals") loadTriage();
  }, [tab, loadTriage]);

  const handoff = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/inbox/${id}/handoff-to-erp`);
      const b = data?.brief || {};
      if (b.email_sent) {
        toast.success("Handed off — brief emailed to you", {
          description: `Full context sent to ${b.email_to}. Quote them in myEuropacific.`,
        });
      } else {
        toast.warning("Handed off — but the brief email failed", {
          description: b.email_error
            ? `Reason: ${b.email_error}`
            : "Couldn't email the brief (check M365 connection).",
          duration: 12000,
        });
      }
      await Promise.all([loadTriage(), loadAttention()]);
      setPendingDMs((p) => p.filter((x) => x.id !== id));
    } catch (e) { toast.error(`Handoff failed: ${e?.response?.data?.detail || "unknown"}`); }
    setBusyId(null);
  };

  const approveQ = async (id, edits = {}) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/sequencer/approve/${id}`, edits);
      if (data.send_result?.ok) toast.success("Approved + invite fired");
      else toast.warning(`Approved, but send failed: ${data.send_result?.error}`);
      await Promise.all([loadTriage(), loadAttention()]);
    } catch (e) { toast.error(`Approval failed: ${e?.response?.data?.detail || "unknown"}`); }
    setBusyId(null);
  };

  const skipReply = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/inbox/${id}/dismiss-reply`);
      setReplies((prev) => prev.filter((p) => p.id !== id));
      setAttentionItems((prev) => prev.filter((x) => !(x.kind === "reply" && x.id === id)));
      setPendingDMs((p) => p.filter((x) => x.id !== id));
      toast.success("Skipped — removed from Hot replies");
    } catch { toast.error("Could not skip"); }
    setBusyId(null);
  };

  const rejectQ = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/sequencer/reject/${id}`);
      toast.success("Skipped");
      await Promise.all([loadTriage(), loadAttention()]);
    } catch (e) { toast.error(`Skip failed: ${e?.response?.data?.detail || "unknown"}`); }
    setBusyId(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === "needs"
        ? "/m365/inbox?lead_only=true&limit=100"
        : "/m365/inbox?limit=100";
      const { data } = await api.get(url);
      setEmails(data.messages || []);
    } catch { toast.error("Failed to load inbox"); }
    setLoading(false);
  }, [tab]);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/m365/inbox/needs-reply-count");
      setNeedsCount(data.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadCount(); }, [loadCount]);

  const skip = async (id, ev) => {
    ev && ev.stopPropagation();
    try {
      await api.post(`/m365/inbox/${id}/dismiss`);
      setEmails((prev) => prev.filter((x) => x.id !== id));
      setAttentionItems((prev) => prev.filter((x) => !(x.kind === "email" && x.id === id)));
      setNeedsCount((c) => Math.max(0, c - 1));
      if (active === id) { setActive(null); setDetail(null); }
      toast.success("Skipped — removed from Needs reply");
    } catch { toast.error("Could not skip"); }
  };

  useEffect(() => { listScrollTop.current = 0; load(); }, [load]);

  const openEmail = async (id) => {
    setActive(id);
    setDetail(null);
    setReplyBody(""); setReplyTo([]); setReplyCc(""); setReplySubject(""); setReplyGuidance("");
    try {
      const { data } = await api.get(`/m365/email/${id}`);
      setDetail(data);
      setReplyTo([data.from_email]);
      setReplySubject(data.subject?.toLowerCase().startsWith("re:") ? data.subject : `Re: ${data.subject || ""}`);
    } catch { toast.error("Failed to load email"); }
  };

  const poll = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/m365/poll-inbox");
      toast.success(`Ingested ${data.ingested} new (fetched ${data.fetched})`);
      await load();
    } catch (e) {
      toast.error(`Poll failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setPolling(false);
  };

  const aiDraft = async () => {
    if (!active) return;
    setDraftBusy(true);
    try {
      const { data } = await api.post(`/m365/email/${active}/ai-reply`, {
        guidance: replyGuidance.trim() || undefined,
        graph_message_id: detail?.graph_message_id || undefined,
      });
      setReplyBody(data.draft_text || "");
      if (data.subject) setReplySubject(data.subject);
      if (data.to?.length) setReplyTo(data.to);
      setReplyGuidance("");
      toast.success("AI draft ready — review then Send");
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("AI budget reached — adjust in AI Budget");
      } else {
        toast.error(`AI draft failed: ${e?.response?.data?.detail || "unknown"}`);
      }
    }
    setDraftBusy(false);
  };

  const sendReply = async () => {
    if (!replyTo.length || !replyBody.trim()) {
      toast.error("Recipient + body required");
      return;
    }
    setSending(true);
    try {
      const html = "<p>" + replyBody.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>") + "</p>";
      const cc = replyCc.split(",").map((s) => s.trim()).filter(Boolean);
      if (active) {
        // Reply IN-THREAD (Graph createReply → lands in the same email string, not a new email).
        await api.post(`/m365/email/${active}/send-reply`, {
          to: replyTo,
          cc,
          html_body: html,
          prospect_id: detail?.prospect_id || undefined,
          graph_message_id: detail?.graph_message_id || undefined,
        });
        toast.success("Reply sent in-thread");
      } else {
        // No source email (shouldn't happen in the reply composer) → fall back to a new email.
        await api.post("/m365/send", { to: replyTo, cc, subject: replySubject, html_body: html });
        toast.success("Sent via Microsoft 365");
      }
      setReplyBody("");
      setNeedsCount((c) => Math.max(0, c - 1));
      await load();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setSending(false);
  };

  return (
    <DashboardLayout
      eyebrow="Inbox"
      title="Unified inbox"
      actions={
        <div className="flex gap-2">
          <Button onClick={() => setComposeOpen(true)} data-testid="m365-compose-btn" className="rounded-sm">
            <PenSquare className="w-4 h-4 mr-1.5" /> Compose
          </Button>
          <Button onClick={poll} disabled={polling} data-testid="m365-inbox-poll-btn" variant="outline" className="rounded-sm">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${polling ? "animate-spin" : ""}`} />
            {polling ? "Polling..." : "Poll inbox"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="inbox-top-tabs">
        <TopTab id="attention" label="Needs attention" icon={ListChecks} active={tab} set={setTab} count={attentionItems.length} />
        <TopTab id="all" label="Email" icon={Mail} active={tab} set={setTab} />
        <TopTab id="needs" label="Needs reply" icon={InboxIcon} active={tab} set={setTab} count={needsCount} />
        <TopTab id="triage" label="Hot replies" icon={Flame} active={tab} set={setTab} count={replies.length} />
        <TopTab id="dms" label="LinkedIn DMs" icon={Linkedin} active={tab} set={setTab} count={pendingDMs.length} />
        <TopTab id="approvals" label="Approvals" icon={ShieldCheck} active={tab} set={setTab} count={approvalQueue.length} />
        <TopTab id="sent" label="Sent" icon={SendHorizontal} active={tab} set={setTab} />
      </div>

      {tab === "attention" && (
        <div className="space-y-3" data-testid="inbox-attention-list">
          <p className="text-xs text-muted-foreground mb-1">
            Everything that needs you today — lead replies, hot replies and approvals — priority-sorted into one list.
          </p>
          {attentionItems.length === 0 ? (
            <div className="data-card text-center py-12">
              <Flame className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">You&apos;re all caught up 🎉</h3>
              <p className="text-xs text-muted-foreground mt-1">No replies or approvals waiting. New ones land here, most urgent first.</p>
            </div>
          ) : attentionItems.map((it) => {
            if (it.kind === "reply") return <ReplyCard key={`r-${it.id}`} prospect={it.data} onHandoff={() => handoff(it.id)} onSkip={() => skipReply(it.id)} busy={busyId === it.id} />;
            if (it.kind === "approval") return <ApprovalCard key={`a-${it.id}`} prospect={it.data} onApprove={(e) => approveQ(it.id, e)} onReject={() => rejectQ(it.id)} busy={busyId === it.id} />;
            return <AttentionEmailCard key={`e-${it.id}`} email={it.data} onOpen={() => { setTab("needs"); openEmail(it.id); }} onSkip={(ev) => skip(it.id, ev)} />;
          })}
        </div>
      )}

      {(tab === "all" || tab === "needs") && (
      <div className="grid lg:grid-cols-12 gap-6 h-[calc(100vh-220px)]">
        {/* Email list */}
        <div className={`lg:col-span-5 border border-border rounded-sm overflow-hidden flex flex-col ${active ? "hidden lg:flex" : ""}`} data-testid="m365-inbox-list">
          <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
            <span className="text-xs font-medium">{tab === "needs" ? "Lead replies needing an answer" : "All email"}</span>
            <span className="text-[0.6rem] text-muted-foreground">{emails.length} · auto-polls 5 min</span>
          </div>
          <div className="flex-1 overflow-y-auto" ref={listRef} onScroll={onListScroll}>
            {loading && <div className="p-6 text-sm text-muted-foreground text-center">Loading...</div>}
            {!loading && emails.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                {tab === "needs"
                  ? "No lead replies waiting. Replies from your prospects show up here."
                  : 'No emails yet. Click "Poll inbox" or connect Microsoft 365 in Settings.'}
              </div>
            )}
            {emails.map((e) => (
              <div
                key={e.id}
                data-testid={`m365-email-row-${e.id}`}
                onClick={() => openEmail(e.id)}
                role="button"
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors cursor-pointer ${
                  active === e.id ? "bg-muted/60" : e.is_read ? "hover:bg-muted/30" : "bg-card hover:bg-muted/30"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${e.is_read ? "" : "font-semibold"}`}>
                    {e.prospect_name || e.from_email}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground whitespace-nowrap">{relativeTime(e.received_at)}</span>
                </div>
                {e.prospect_name && (
                  <div className="text-[0.65rem] text-signal-500 truncate">{e.from_email}{e.prospect_company ? ` · ${e.prospect_company}` : ""}</div>
                )}
                <div className="text-xs truncate mt-0.5">{e.subject || "(no subject)"}</div>
                <div className="text-xs text-muted-foreground truncate line-clamp-1 mt-0.5">{e.body_preview}</div>
                {tab === "needs" && (
                  <div className="mt-1.5 flex items-center gap-2">
                    {(() => { const s = slaBadge(e.received_at); return s ? (
                      <span className={`text-[0.6rem] px-1.5 py-0.5 rounded-sm border ${s.cls}`} data-testid={`m365-sla-${e.id}`}>{s.label}</span>
                    ) : null; })()}
                    <button onClick={(ev) => skip(e.id, ev)} data-testid={`m365-skip-${e.id}`}
                      className="text-[0.65rem] text-muted-foreground hover:text-foreground underline">
                      Skip
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Detail + reply */}
        <div className={`lg:col-span-7 border border-border rounded-sm flex flex-col bg-card/30 ${!active ? "hidden lg:flex" : ""}`} data-testid="m365-inbox-detail">
          {!detail && active && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Loading email...</div>}
          {!detail && !active && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select an email to view + reply</div>}
          {detail && (
            <>
              <div className="px-5 py-3 border-b border-border">
                <button onClick={() => { setActive(null); setDetail(null); }} className="lg:hidden inline-flex items-center text-xs text-muted-foreground mb-2 hover:text-foreground">
                  <ArrowLeft className="w-3 h-3 mr-1" /> Back to list
                </button>
                <div className="font-display font-semibold text-base">{detail.subject || "(no subject)"}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <User className="w-3 h-3" /> <span className="font-mono">{detail.from_email}</span>
                  <span className="ml-2">{detail.received_at && new Date(detail.received_at).toLocaleString()}</span>
                </div>
                {detail.to_recipients && detail.to_recipients.length > 0 && (
                  <div className="text-[0.65rem] text-muted-foreground mt-0.5">to: {detail.to_recipients.join(", ")}</div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                {detail.body && detail.body_content_type?.toLowerCase() === "html" ? (
                  <div
                    className="prose prose-invert prose-sm max-w-none email-body"
                    data-testid="m365-email-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.body) }}
                  />
                ) : (
                  <pre className="text-sm whitespace-pre-wrap font-sans" data-testid="m365-email-body-text">
                    {detail.body || detail.body_preview || "(empty body)"}
                  </pre>
                )}
                {detail.body_fetch_error && (
                  <div className="text-xs text-amber-accent mt-3">⚠ Full body fetch failed: {detail.body_fetch_error}</div>
                )}
              </div>

              {/* Reply composer */}
              <div className="border-t border-border p-4 pb-20 space-y-2.5 bg-background/40">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">To</span>
                  <Input className="col-span-11 h-8 text-xs"
                         value={replyTo.join(", ")}
                         onChange={(e) => setReplyTo(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                         data-testid="m365-reply-to" />
                  <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">Cc</span>
                  <Input className="col-span-11 h-8 text-xs"
                         placeholder="optional, comma-separated"
                         value={replyCc}
                         onChange={(e) => setReplyCc(e.target.value)}
                         data-testid="m365-reply-cc" />
                  <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">Subj</span>
                  <Input className="col-span-11 h-8 text-xs"
                         value={replySubject}
                         onChange={(e) => setReplySubject(e.target.value)}
                         data-testid="m365-reply-subject" />
                </div>
                <div className="rounded-sm border border-dashed border-signal-500/40 bg-signal-500/5 px-3 py-2 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-signal-500" />
                    <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-medium">Guidance for AI draft</span>
                    <span className="text-[0.62rem] text-muted-foreground italic">— internal only, never sent to the customer</span>
                  </div>
                  <textarea
                    data-testid="m365-reply-guidance"
                    rows={2}
                    value={replyGuidance}
                    onChange={(e) => setReplyGuidance(e.target.value)}
                    placeholder="Optional notes to steer the draft, e.g. 'Offer Koper routing, ask for monthly volumes & incoterm, keep it under 5 sentences.'"
                    className="w-full px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500 resize-none"
                  />
                </div>
                <textarea
                  rows={6}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write a reply — or click 'AI draft' to have GPT-5.2 draft one for you grounded on this email."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
                />
                <div className="flex items-center justify-between">
                  {(() => {
                    const sender = (detail?.from_email || "").toLowerCase();
                    const isBotMail = /^(noreply|no-reply|donotreply|do-not-reply|notifications?|bookings?|security-noreply|mailer-daemon|postmaster|automated|system|alerts?|info)@/i.test(sender)
                      || sender.endsWith("@cma-cgm.com") || sender.includes("noreply");
                    return isBotMail ? (
                      <div className="text-[0.7rem] text-muted-foreground italic" data-testid="m365-ai-draft-skipped">
                        Automated sender — AI draft hidden to save tokens
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={aiDraft} disabled={draftBusy} data-testid="m365-ai-draft-btn">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        {draftBusy ? "Drafting..." : "AI draft reply"}
                      </Button>
                    );
                  })()}
                  <Button size="sm" onClick={sendReply} disabled={sending || !replyBody.trim()} data-testid="m365-send-reply-btn">
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {sending ? "Sending..." : "Send via Microsoft 365"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {tab === "dms" && (
        <div className="space-y-3" data-testid="inbox-dms-list">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">AI-drafted replies to your LinkedIn chats & accepted invitations — edit, then send. Nothing sends without your click. Newest first.</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setHideOldDMs((v) => !v)} data-testid="dms-hide-old-toggle"
                className={`text-xs rounded-sm border px-2 py-1 transition-colors ${hideOldDMs ? "border-signal-500 text-signal-500 bg-signal-500/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {hideOldDMs ? "Showing recent (≤30d)" : "Hide DMs older than 30 days"}
              </button>
              <Button size="sm" variant="outline" onClick={syncDMs} disabled={dmSyncing} className="rounded-sm" data-testid="dms-sync-btn">
                <Linkedin className="w-3.5 h-3.5 mr-1.5" />{dmSyncing ? "Syncing…" : "Sync now"}
              </Button>
            </div>
          </div>
          {(() => {
            const dmTs = (p) => { const t = new Date(p.last_reply_at || p.replied_at || 0).getTime(); return isNaN(t) ? 0 : t; };
            let list = [...pendingDMs].sort((a, b) => dmTs(b) - dmTs(a));
            if (hideOldDMs) list = list.filter((p) => { const t = dmTs(p); return t > 0 && (Date.now() - t) <= 30 * 86400000; });
            if (list.length === 0) {
              return (
                <div className="data-card text-center py-16">
                  <Linkedin className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <h3 className="font-display font-semibold">No drafted DMs</h3>
                  <p className="text-xs text-muted-foreground mt-1">{hideOldDMs && pendingDMs.length > 0 ? "All drafted DMs are older than 30 days — turn off the filter to see them." : "Hit \"Sync now\" to pull your latest LinkedIn replies & invitations and draft responses."}</p>
                </div>
              );
            }
            return list.map((p) => (
              <DMDraftCard key={p.id} dm={p} busy={busyId === p.id} onSend={sendDM} onSkip={skipDM} onRegen={regenerateDM} bookingLink={bookingLink} />
            ));
          })()}
        </div>
      )}

      {tab === "triage" && (
        <div className="space-y-3" data-testid="inbox-triage-list">
          {replies.length === 0 ? (
            <div className="data-card text-center py-12">
              <Flame className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">No replies to triage</h3>
              <p className="text-xs text-muted-foreground mt-1">When leads reply to your LinkedIn or email outreach, the rich triage (intent, Quote Brain, ERP history) shows here.</p>
            </div>
          ) : replies.map((p) => (
            <ReplyCard key={p.id} prospect={p} onHandoff={() => handoff(p.id)} onSkip={() => skipReply(p.id)} busy={busyId === p.id} />
          ))}
        </div>
      )}

      {tab === "approvals" && (
        <div className="space-y-3" data-testid="inbox-approvals-list">
          {approvalQueue.length === 0 ? (
            <div className="data-card text-center py-12">
              <ShieldCheck className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <h3 className="font-display font-semibold">Nothing to approve</h3>
              <p className="text-xs text-muted-foreground mt-1">High-confidence sends fire automatically. Risky drafts (low-fit / money keywords) land here for your decision.</p>
            </div>
          ) : approvalQueue.map((p) => (
            <ApprovalCard key={p.id} prospect={p} onApprove={(e) => approveQ(p.id, e)} onReject={() => rejectQ(p.id)} busy={busyId === p.id} />
          ))}
        </div>
      )}

      {tab === "sent" && (
        <div className="space-y-3" data-testid="inbox-sent-list">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5" data-testid="sent-filters">
              {[
                { id: "all", label: "All" },
                { id: "cold", label: "AI cold emails" },
                { id: "human", label: "Sent by me" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSentFilter(f.id)}
                  data-testid={`sent-filter-${f.id}`}
                  className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                    sentFilter === f.id ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={loadSent} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="sent-refresh">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          <div className="grid lg:grid-cols-12 gap-6 h-[calc(100vh-260px)]">
            {/* Sent list */}
            <div className={`lg:col-span-5 border border-border rounded-sm overflow-hidden flex flex-col ${sentActive ? "hidden lg:flex" : ""}`} data-testid="sent-list-pane">
              <div className="flex-1 overflow-y-auto">
                {sentLoading && <div className="p-6 text-sm text-muted-foreground text-center">Loading sent emails…</div>}
                {!sentLoading && sent.length === 0 && (
                  <div className="p-6 text-center">
                    <SendHorizontal className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <h3 className="font-display font-semibold text-sm">No sent emails yet</h3>
                    <p className="text-xs text-muted-foreground mt-1">Autonomous cold emails and replies you send in the loop will appear here.</p>
                  </div>
                )}
                {!sentLoading && sent.map((m, i) => (
                  <div
                    key={m.id || i}
                    onClick={() => m.id && openSent(m.id)}
                    role="button"
                    data-testid={`sent-row-${m.id || i}`}
                    className={`px-4 py-3 border-b border-border transition-colors cursor-pointer ${sentActive === m.id ? "bg-muted/60" : "hover:bg-muted/30"}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={`text-[0.6rem] px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 shrink-0 ${
                            m.kind === "cold"
                              ? "border-signal-500/40 text-signal-500 bg-signal-500/5"
                              : "border-emerald-500/40 text-emerald-500 bg-emerald-500/5"
                          }`}
                          data-testid={`sent-kind-${m.id || i}`}
                        >
                          {m.kind === "cold" ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
                          {m.kind === "cold" ? "AI cold email" : "Sent by me"}
                        </span>
                        <span className="text-sm font-medium truncate">{m.prospect_name || (m.to || []).join(", ") || "—"}</span>
                      </div>
                      <span className="text-[0.65rem] text-muted-foreground whitespace-nowrap">{relativeTime(m.sent_at)}</span>
                    </div>
                    <div className="text-[0.65rem] text-muted-foreground truncate">to: {(m.to || []).join(", ")}</div>
                    <div className="text-xs mt-1 truncate">{m.subject || "(no subject)"}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{m.body_preview}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sent detail + follow-up */}
            <div className={`lg:col-span-7 border border-border rounded-sm flex flex-col bg-card/30 ${!sentActive ? "hidden lg:flex" : ""}`} data-testid="sent-detail-pane">
              {!sentDetail && sentActive && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Loading email…</div>}
              {!sentDetail && !sentActive && <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a sent email to read + follow up</div>}
              {sentDetail && (
                <>
                  <div className="px-5 py-3 border-b border-border">
                    <button onClick={() => { setSentActive(null); setSentDetail(null); }} className="lg:hidden inline-flex items-center text-xs text-muted-foreground mb-2 hover:text-foreground">
                      <ArrowLeft className="w-3 h-3 mr-1" /> Back to list
                    </button>
                    <div className="font-display font-semibold text-base">{sentDetail.subject || "(no subject)"}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[0.6rem] px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${sentDetail.kind === "cold" ? "border-signal-500/40 text-signal-500 bg-signal-500/5" : "border-emerald-500/40 text-emerald-500 bg-emerald-500/5"}`}>
                        {sentDetail.kind === "cold" ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {sentDetail.kind === "cold" ? "AI cold email" : "Sent by me"}
                      </span>
                      <span className="font-mono">to: {(sentDetail.to || []).join(", ")}</span>
                      <span className="ml-2">{sentDetail.sent_at && new Date(sentDetail.sent_at).toLocaleString()}</span>
                    </div>
                    {sentDetail.from && <div className="text-[0.65rem] text-muted-foreground mt-0.5">from: {sentDetail.from}</div>}
                  </div>

                  <div className="flex-1 overflow-y-auto p-5">
                    {sentDetail.body_html ? (
                      <div
                        className="prose prose-invert prose-sm max-w-none email-body"
                        data-testid="sent-email-body"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(sentDetail.body_html) }}
                      />
                    ) : (
                      <pre className="text-sm whitespace-pre-wrap font-sans" data-testid="sent-email-body-text">{sentDetail.body_preview || "(no stored body)"}</pre>
                    )}
                  </div>

                  {/* Follow-up composer */}
                  <div className="border-t border-border p-4 pb-20 space-y-2.5 bg-background/40">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">To</span>
                      <Input className="col-span-11 h-8 text-xs" value={sentReplyTo.join(", ")}
                        onChange={(e) => setSentReplyTo(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="sent-reply-to" />
                      <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">Cc</span>
                      <Input className="col-span-11 h-8 text-xs" placeholder="optional, comma-separated" value={sentReplyCc}
                        onChange={(e) => setSentReplyCc(e.target.value)} data-testid="sent-reply-cc" />
                      <span className="col-span-1 text-[0.65rem] tracking-widest uppercase text-muted-foreground">Subj</span>
                      <Input className="col-span-11 h-8 text-xs" value={sentReplySubject}
                        onChange={(e) => setSentReplySubject(e.target.value)} data-testid="sent-reply-subject" />
                    </div>
                    <div className="rounded-sm border border-dashed border-signal-500/40 bg-signal-500/5 px-3 py-2 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-signal-500" />
                        <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-medium">Guidance for AI follow-up</span>
                        <span className="text-[0.62rem] text-muted-foreground italic">— internal only, never sent to the customer</span>
                      </div>
                      <textarea data-testid="sent-reply-guidance" rows={2} value={sentReplyGuidance}
                        onChange={(e) => setSentReplyGuidance(e.target.value)}
                        placeholder="Optional notes to steer the follow-up, e.g. 'gentle nudge, ask if they have shipments through Koper next month.'"
                        className="w-full px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500 resize-none" />
                    </div>
                    <textarea rows={6} value={sentReplyBody} onChange={(e) => setSentReplyBody(e.target.value)} data-testid="sent-reply-body"
                      placeholder="Write a follow-up — or click 'AI draft' to generate one grounded on this sent email."
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" />
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={aiDraftSent} disabled={sentDraftBusy} data-testid="sent-ai-draft-btn">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        {sentDraftBusy ? "Drafting…" : "AI draft follow-up"}
                      </Button>
                      <Button size="sm" onClick={sendFollowUp} disabled={sentSending || !sentReplyBody.trim()} data-testid="sent-send-followup-btn">
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {sentSending ? "Sending…" : "Send via Microsoft 365"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {composeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4"
          onClick={() => !composeSending && setComposeOpen(false)}
          data-testid="m365-compose-modal"
        >
          <div
            className="bg-card border border-border rounded-t-md md:rounded-sm w-full max-w-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-display font-semibold inline-flex items-center gap-2">
                <PenSquare className="w-4 h-4 text-signal-500" /> New email
              </div>
              <button
                onClick={() => !composeSending && setComposeOpen(false)}
                disabled={composeSending}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[0.65rem] tracking-widest uppercase text-muted-foreground block mb-1">To</label>
                <Input
                  data-testid="m365-compose-to"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  placeholder="recipient@example.com (comma-separated for multiple)"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[0.65rem] tracking-widest uppercase text-muted-foreground block mb-1">Subject</label>
                <Input
                  data-testid="m365-compose-subject"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="A clear, specific subject"
                />
              </div>
              <div>
                <label className="text-[0.65rem] tracking-widest uppercase text-muted-foreground block mb-1">Body</label>
                <textarea
                  data-testid="m365-compose-body"
                  rows={8}
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  placeholder="Write your message. Your signature appends automatically."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <p className="text-[0.65rem] text-muted-foreground">
                  Sent FROM your connected M365 mailbox (or the "Send As" alias set in Settings).
                </p>
                <Button
                  size="sm"
                  onClick={async () => {
                    const to = composeTo.split(",").map((s) => s.trim()).filter(Boolean);
                    if (!to.length || !composeSubject.trim() || !composeBody.trim()) {
                      toast.error("To, subject + body required");
                      return;
                    }
                    setComposeSending(true);
                    try {
                      const html = "<p>" + composeBody.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>") + "</p>";
                      const { data } = await api.post("/m365/send", {
                        to,
                        subject: composeSubject,
                        html_body: html,
                      });
                      if (data?.queued) {
                        toast.success("Still sending…", { description: data.message || "Your email is being sent in the background. Check the Sent tab shortly." });
                      } else {
                        toast.success(`Sent to ${to.join(", ")}`);
                      }
                      setComposeOpen(false);
                      setComposeTo(""); setComposeSubject(""); setComposeBody("");
                    } catch (e) {
                      const d = e?.response?.data?.detail || "unknown";
                      toast.error(/^send failed/i.test(d) ? d : `Send failed: ${d}`);
                    }
                    setComposeSending(false);
                  }}
                  disabled={composeSending || !composeBody.trim()}
                  data-testid="m365-compose-send-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {composeSending ? "Sending..." : "Send via Microsoft 365"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

// Lightweight HTML sanitizer — strips scripts, iframes, on* event handlers, javascript: urls
function scoreAttention(it) {
  if (it.kind === "reply") {
    const m = { partnership_inquiry: 100, quote_request: 90, scheduling: 65, objection: 60, general: 45, unclear: 45, not_interested: 20 };
    return m[it.data.last_reply_intent] ?? 50;
  }
  if (it.kind === "approval") return 70 + (it.data.score || 0) / 20;
  if (it.kind === "email") {
    const hrs = it.ts ? (Date.now() - new Date(it.ts).getTime()) / 3600000 : 0;
    return 50 + (hrs >= 48 ? 30 : hrs >= 24 ? 15 : 0);
  }
  return 40;
}

function AttentionEmailCard({ email, onOpen, onSkip }) {
  const s = slaBadge(email.received_at);
  return (
    <div className="data-card border-signal-500/30" data-testid={`attention-email-${email.id}`}>
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm inline-flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-signal-500" />
            {email.prospect_name || email.from_email}
            <span className="px-1.5 py-0.5 rounded-sm text-[0.55rem] tracking-widest uppercase font-mono border bg-signal-500/10 text-signal-500 border-signal-500/40">Email reply</span>
          </div>
          <div className="text-[0.65rem] text-muted-foreground truncate">{email.from_email}{email.prospect_company ? ` · ${email.prospect_company}` : ""}</div>
        </div>
        {s && <span className={`text-[0.6rem] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${s.cls}`}>{s.label}</span>}
      </div>
      <div className="text-xs font-medium mb-0.5">{email.subject || "(no subject)"}</div>
      <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{email.body_preview}</div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onOpen} data-testid={`attention-open-${email.id}`} className="rounded-sm">
          <Send className="w-3.5 h-3.5 mr-1.5" /> Open &amp; reply
        </Button>
        <Button size="sm" variant="ghost" onClick={onSkip} data-testid={`attention-skip-${email.id}`}>Skip</Button>
      </div>
    </div>
  );
}

function TopTab({ id, label, icon: Icon, active, set, count }) {
  const isActive = active === id;
  return (
    <button onClick={() => set(id)} data-testid={`inbox-top-${id}`}
      className={`text-xs px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 transition-colors ${
        isActive ? "border-signal-500 bg-signal-500 text-white" : "border-border text-muted-foreground hover:text-foreground"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
      {count > 0 && <span className={`text-[0.6rem] px-1.5 rounded-full ${isActive ? "bg-white/25" : "bg-muted text-foreground"}`}>{count}</span>}
    </button>
  );
}

function DMDraftCard({ dm, busy, onSend, onSkip, onRegen, bookingLink }) {
  const [text, setText] = useState((dm.drafts || {}).dm || "");
  const [tone, setTone] = useState("");
  const [regen, setRegen] = useState(false);
  useEffect(() => { setText((dm.drafts || {}).dm || ""); }, [dm.drafts]);

  const doRegen = async () => {
    setRegen(true);
    try { const t = await onRegen(dm.id, tone || undefined); if (t) setText(t); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate"); }
    setRegen(false);
  };
  const insertBooking = () => {
    if (!bookingLink) { toast.error("Add a meeting link in Settings first"); return; }
    const sep = text && !text.endsWith("\n") ? "\n\n" : "";
    setText((t) => `${t}${sep}Happy to jump on a quick call — you can grab a slot here: ${bookingLink}`);
  };

  const ts = dm.last_reply_at || dm.replied_at;
  let dateLabel = "", ageDays = null, dateTitle = "";
  if (ts) {
    const d = new Date(ts);
    if (!isNaN(d)) {
      ageDays = Math.floor((Date.now() - d.getTime()) / 86400000);
      dateTitle = d.toLocaleString();
      const rel = ageDays <= 0 ? "today" : ageDays === 1 ? "yesterday" : ageDays < 30 ? `${ageDays}d ago` : ageDays < 365 ? `${Math.floor(ageDays / 30)}mo ago` : `${Math.floor(ageDays / 365)}y ago`;
      dateLabel = `${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · ${rel}`;
    }
  }
  const isOld = ageDays != null && ageDays > 21;
  return (
    <div className="data-card" data-testid={`dm-card-${dm.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {dm.linkedin_url ? <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-signal-500 inline-flex items-center gap-1">{dm.full_name} <ExternalLink className="w-3 h-3" /></a> : dm.full_name}
          </div>
          <div className="label-eyebrow flex items-center gap-2 flex-wrap">
            <span>{dm.dm_kind === "welcome" ? "New connection · welcome" : "Chat reply"}{dm.company ? ` · ${dm.company}` : ""}</span>
            {dateLabel && (
              <span title={dateTitle} data-testid={`dm-date-${dm.id}`}
                className={`inline-flex items-center gap-1 normal-case tracking-normal ${isOld ? "text-amber-accent" : "text-muted-foreground"}`}>
                <Clock className="w-3 h-3" /> {dateLabel}{isOld ? " — old" : ""}
              </span>
            )}
          </div>
        </div>
        <Linkedin className="w-4 h-4 text-signal-500 flex-shrink-0" />
      </div>
      {dm.last_reply_excerpt && (
        <div className="text-xs text-muted-foreground bg-muted/30 border-l-2 border-border pl-2 py-1 mb-2 line-clamp-3 whitespace-pre-wrap">{dm.last_reply_excerpt}</div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
        data-testid={`dm-draft-${dm.id}`}
        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" />
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <Button size="sm" onClick={() => onSend(dm.id, text)} disabled={busy || regen || !text.trim()} className="rounded-sm" data-testid={`dm-send-${dm.id}`}>
          <Send className="w-3.5 h-3.5 mr-1.5" />{busy ? "Sending…" : "Send DM"}
        </Button>
        <select value={tone} onChange={(e) => setTone(e.target.value)} disabled={regen || busy}
          data-testid={`dm-tone-${dm.id}`}
          className="text-xs bg-background border border-border rounded-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-signal-500">
          <option value="">Default tone</option>
          <option value="professional">Professional</option>
          <option value="warm">Warm</option>
          <option value="direct">Direct</option>
          <option value="casual">Casual</option>
          <option value="formal">Formal</option>
        </select>
        <Button size="sm" variant="outline" onClick={doRegen} disabled={regen || busy} className="rounded-sm" data-testid={`dm-regen-${dm.id}`}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regen ? "animate-spin" : ""}`} />{regen ? "Rewriting…" : "Regenerate"}
        </Button>
        <Button size="sm" variant="ghost" onClick={insertBooking} disabled={busy || regen} className="rounded-sm text-signal-500" data-testid={`dm-book-call-${dm.id}`}>
          <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Book a call
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onSkip(dm.id)} disabled={busy || regen} className="rounded-sm text-muted-foreground" data-testid={`dm-skip-${dm.id}`}>
          <X className="w-3.5 h-3.5 mr-1" /> Skip
        </Button>
      </div>
    </div>
  );
}


function slaBadge(receivedAt) {
  if (!receivedAt) return null;
  const hrs = (Date.now() - new Date(receivedAt).getTime()) / 3600000;
  if (hrs >= 48) return { label: "48h+ · reply now", cls: "text-destructive border-destructive/50 bg-destructive/10" };
  if (hrs >= 24) return { label: "24h+ · follow up", cls: "text-amber-accent border-amber-accent/50 bg-amber-accent/10" };
  return { label: "within SLA", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5" };
}

function sanitizeHtml(html) {
  if (!html) return "";
  // Industry-standard sanitization of untrusted inbound email HTML (DOMPurify).
  // Strips scripts, event handlers, javascript:/data: URIs, <iframe>/<object>/<embed>,
  // etc. Links open in a new tab safely (target=_blank + rel=noopener).
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target"],
    FORBID_TAGS: ["style", "form", "input", "button"],
    FORBID_ATTR: ["srcset"],
  });
  return clean;
}
